import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  Chip,
  Button,
  LinearProgress,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Icons
import DescriptionIcon from "@mui/icons-material/Description";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const DoctorWelcomeIllustration = () => {
  // ... your existing SVG code (unchanged)
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: { xs: "flex-start", sm: "center" },
        alignItems: "center",
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 200 200"
        sx={{
          width: { xs: 120, sm: 160, md: 180 },
          height: { xs: 120, sm: 160, md: 180 },
          filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.2))",
        }}
      >
        <circle cx="100" cy="100" r="85" fill="rgba(255,255,255,0.1)" />
        <path
          d="M100 40 L145 55 L145 105 C145 135, 125 155, 100 165 C75 155, 55 135, 55 105 L55 55 Z"
          fill="rgba(255,255,255,0.95)"
        />
        <rect x="90" y="70" width="20" height="60" rx="4" fill="#5651F4" />
        <rect x="70" y="90" width="60" height="20" rx="4" fill="#5651F4" />
        <circle cx="100" cy="100" r="6" fill="#EC4899" />
        <circle cx="50" cy="50" r="4" fill="rgba(255,255,255,0.5)" />
        <circle cx="160" cy="70" r="5" fill="rgba(255,255,255,0.4)" />
        <circle cx="150" cy="140" r="4" fill="rgba(255,255,255,0.5)" />
      </Box>
    </Box>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [patientName, setPatientName] = useState("Patient");
  const [currentTime, setCurrentTime] = useState("");

  // ✅ NEW STATES for API data
  const [summary, setSummary] = useState({
    totalForms: 0,
    signedForms: 0,
    unsignedForms: 0,
  });
  const [todayForms, setTodayForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedName = localStorage.getItem("neuro_patientportal_userName");
    if (storedName) setPatientName(storedName);

    const hour = new Date().getHours();
    if (hour < 12) setCurrentTime("Good Morning");
    else if (hour < 18) setCurrentTime("Good Afternoon");
    else setCurrentTime("Good Evening");

    // ✅ FETCH DASHBOARD DATA FROM API
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("neuro_patientportal_auth_token"); // adjust key if different

      const response = await axios.get(
        "https://api.neurohhax.devforhealth.com/api/PatientPortal/Dashboard", // ⚠️ change to your actual endpoint
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data;

      if (data?.summary?.length > 0) {
        setSummary(data.summary[0]);
      }
      if (data?.todayForms) {
        setTodayForms(data.todayForms);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ 3 SUMMARY CARDS DATA
  const summaryCards = [
    {
      title: "Total Forms",
      value: summary.totalForms,
      subtitle: "All Forms",
      icon: <AssignmentIcon sx={{ fontSize: 28 }} />,
      gradient: "linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)",
    },
    {
      title: "Signed Forms",
      value: summary.signedForms,
      subtitle: "Completed",
      icon: <TaskAltIcon sx={{ fontSize: 28 }} />,
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    },
    {
      title: "Unsigned Forms",
      value: summary.unsignedForms,
      subtitle: "Action Required",
      icon: <PendingActionsIcon sx={{ fontSize: 28 }} />,
      gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
    },
  ];

  const quickActions = [
    {
      title: "My Forms",
      description: "View medical forms",
      icon: <DescriptionIcon />,
      color: "#5651F4",
      bgColor: "#EEEDFE",
      route: "/dashboard/forms",
    },
    {
      title: "My Profile",
      description: "Personal information",
      icon: <PersonIcon />,
      color: "#EC4899",
      bgColor: "#FCE7F3",
      route: "/dashboard/patient-profile",
    },
  ];

  return (
    <Box
      sx={{
       
        bgcolor: "#F8FAFC",
        minHeight: "100vh",
        borderRadius: "10px",
        // ✅ FIX #1 — Page background dark mode
        ".dark-theme &": {
          bgcolor: "#111827 !important",
        },
      }}
      className="top-navbar"
    >
      {/* ═══════════════════════════════════════ */}
      {/* 👋 WELCOME HEADER (unchanged)            */}
      {/* ═══════════════════════════════════════ */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          background: "linear-gradient( #252194 50%, #3F3DB8 100%)",
          color: "white",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 8px 32px rgba(86, 81, 244, 0.25)",
         margin:"12px"
        }}
      >
        <Box sx={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(194, 12, 12, 0.08)" }} />
        <Box sx={{ position: "absolute", bottom: -80, right: 100, width: 250, height: 250, borderRadius: "50%", background: "rgba(194, 24, 24, 0.05)" }} />

        <CardContent sx={{ p: { xs: 3, sm: 4 }, position: "relative", zIndex: 1 }}>
          <Grid container spacing={2} alignItems="center" sx={{ display: "flex", justifyContent: "space-between" }}>
            <Grid item xs={12} sm={8}>
              <Typography sx={{ color: "#FFFFFF !important", opacity: 1, mb: 0.5, fontSize: { xs: "0.9rem", sm: "1rem" }, fontWeight: 600 }}>
                {currentTime}
              </Typography>
              <Typography sx={{ color: "#FFFFFF !important", fontWeight: 800, mb: 1, fontSize: { xs: "1.5rem", sm: "2rem", md: "2.25rem" } }}>
                Welcome back, {patientName}!
              </Typography>
              <Typography sx={{ color: "#FFFFFF !important", opacity: 1, fontSize: { xs: "0.875rem", sm: "1rem" }, maxWidth: "500px", fontWeight: 400 }}>
                Access your profile and healthcare forms here.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-end" }, alignItems: "center" }}>
              <DoctorWelcomeIllustration />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════ */}
      {/* 📊 SUMMARY CARDS (unchanged)              */}
      {/* ═══════════════════════════════════════ */}
      <Grid container spacing={2} sx={{ display: "flex", justifyContent: { sm: "space-between", }, m: 1.5 }}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index} sx={{ width: { xs: "100%", sm: "100%", md: "32%" } }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: card.gradient,
                color: "#fff",
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 28px rgba(0,0,0,0.15)",
                },
                ".dark-theme &": {
                  color: "#FFF !important",

                },
              }}
            >
              <CardContent sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
                {/* Icon Box */}
                <Box
                  sx={{
                    width: 66,
                    height: 64,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>

                {/* Text Info */}

                <Box sx={{ flex: 1 }}>
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "1.75rem",
                        fontWeight: 800,
                        color: "#FFFFFF !important",
                        lineHeight: 1,
                        ".dark-theme &": {
                          color: "#FFFFFF !important",
                        },
                      }}
                    >
                      {card.value}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      color: "#FFFFFF !important",
                      mt: 0.5,
                      ".dark-theme &": {
                        color: "#FFFFFF !important",
                      },
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "rgba(255,255,255,0.85) !important",
                      fontWeight: 500,
                      ".dark-theme &": {
                        color: "rgba(255,255,255,0.85) !important",
                      },
                    }}
                  >
                    {card.subtitle}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ═══════════════════════════════════════ */}
      {/* 📋 TODAY'S FORMS LIST                     */}
      {/* ═══════════════════════════════════════ */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #E5E7EB",
          m: "12px",
          overflow: "hidden",
          ".dark-theme &": {
            backgroundColor: "#1a1a2e !important",
            border: "1px solid rgba(255,255,255,0.08) !important",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)",
            ".dark-theme &": {
              background: "linear-gradient(135deg, #16162e 0%, #1e1e3f 100%) !important",
              borderBottom: "1px solid rgba(255,255,255,0.08) !important",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: "#5651F4",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <CalendarMonthIcon />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: "#1F2937",
                  ".dark-theme &": {
                    color: "#FFFFFF !important",
                  },
                }}
              >
                Today's Forms
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "#6B7280",
                  ".dark-theme &": {
                    color: "rgba(255,255,255,0.6) !important",
                  },
                }}
              >
                Form updated today
              </Typography>
            </Box>
          </Box>

          <Chip
            label={`${todayForms.length} Forms`}
            sx={{
              backgroundColor: "#5651F4",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        </Box>

        {/* Forms List */}
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} sx={{ color: "#5651F4" }} />
            </Box>
          ) : todayForms.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <DescriptionIcon sx={{
                fontSize: 48,
                color: "#D1D5DB",
                mb: 1,
                ".dark-theme &": {
                  color: "rgba(255,255,255,0.3) !important",
                },
              }} />
              <Typography
                sx={{
                  color: "#6B7280",
                  fontSize: "0.9rem",
                  ".dark-theme &": {
                    color: "rgba(255,255,255,0.6) !important",
                  },
                }}
              >
                No forms created today
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {todayForms.map((form) => (
                <Grid item xs={12} sm={6} md={4} key={form.id}>
                  <Card
                    elevation={0}
                    onClick={() =>
                      navigate("/dashboard/forms", {
                        state: {
                          openFormId: form.id,
                          openFormTitle: form.formName,
                        },
                      })
                    }
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #E5E7EB",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      // ✅ FIX #3 — Form item card dark mode
                      ".dark-theme &": {
                        backgroundColor: "#111827 !important",
                        border: "1px solid rgba(255,255,255,0.08) !important",
                      },
                      "&:hover": {
                        borderColor: "#5651F4",
                        backgroundColor: "#F9FAFB",
                        transform: "translateX(4px)",
                        ".dark-theme &": {
                          backgroundColor: "rgba(255,255,255,0.05) !important",
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: "#EEEDFE",
                          color: "#5651F4",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          ".dark-theme &": {
                            backgroundColor: "rgba(86,81,244,0.2) !important",
                            color: "#8B87FF !important",
                          },
                        }}
                      >
                        <DescriptionIcon fontSize="small" />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            color: "#1F2937",
                            mb: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            ".dark-theme &": {
                              color: "#FFFFFF !important",
                            },
                          }}
                        >
                          {form.formName}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        
                          <Typography sx={{
                            fontSize: "0.75rem",
                            color: "#6B7280",
                            ".dark-theme &": {
                              color: "#FFFFFF !important",
                            },
                          }}>
                           <span sx={{color: "#1F2937",  }}> Sign Date:{" "}</span>
                            {form.signDate}
                          </Typography>
                        </Box>

                        {/* If your API returns guardian name, uncomment this */}
                        {form.legalGuardianName && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
                            <Typography sx={{
                              fontSize: "0.75rem",
                              color: "#6B7280",
                              ".dark-theme &": {
                                color: "rgba(255,255,255,0.7) !important",
                              },
                            }}>
                              {form.legalGuardianName}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Card>

      {/* ═══════════════════════════════════════ */}
      {/* ⚡ QUICK ACTIONS                          */}
      {/* ═══════════════════════════════════════ */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "#1F2937",
          ml: 2,
          fontSize: { xs: "1rem", sm: "1.125rem" },
          ".dark-theme &": { color: "#FFFFFF !important" },
        }}
      >
        Quick Actions
      </Typography>
      <Grid container spacing={2} sx={{ display: "flex", justifyContent: "space-between", margin: "12px" }}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={6} key={index} sx={{ width: { xs: "100%", md: "49%" } }}>
            <Card
              onClick={() => navigate(action.route)}
              elevation={0}
              sx={{
                backgroundColor: "#f8f8f8",
                borderRadius: 3,
                cursor: "pointer",
                transition: "all 0.3s ease",
                border: "1px solid #9e9e9e",
                height: "100%",
                // ✅ FIX #4 — Quick Actions card dark mode
                ".dark-theme &": {
                  backgroundColor: "#1F2937 !important",
                  border: "1px solid rgba(255,255,255,0.1) !important",
                },
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: `0 8px 20px ${action.color}25`,
                  borderColor: action.color,
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: action.bgColor,
                    color: action.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                    ".dark-theme &": {
                      backgroundColor: `${action.color}33 !important`,
                    },
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant="subtitle1" sx={{
                  fontWeight: 700,
                  color: "#1F2937",
                  mb: 0.5,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  ".dark-theme &": {
                    color: "#FFFFFF !important",
                  },
                }}>
                  {action.title}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "#6B7280",
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  ".dark-theme &": {
                    color: "rgba(255,255,255,0.7) !important",
                  },
                }}>
                  {action.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;